import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Gift, CheckCircle, Download } from "lucide-react";
import { API } from "@/App";
import { useLanguage } from "@/LanguageContext";

const GiftCardSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cardCode = searchParams.get("code");
  const { t } = useLanguage();

  const handleDownloadPDF = () => {
    if (cardCode) {
      window.open(`${API}/gift-cards/${cardCode}/pdf`, "_blank");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#f4e7d8" }}>
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-12 text-center"
          style={{ border: "2px solid #d4af37" }}
          data-testid="giftcard-success-container"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
            style={{ background: "#721111" }}
          >
            <Gift className="h-12 w-12" style={{ color: "#d4af37" }} />
          </motion.div>

          <div className="flex items-center justify-center gap-2 mb-4">
            <CheckCircle className="h-6 w-6" style={{ color: "#006400" }} />
            <h1
              className="text-3xl sm:text-4xl font-serif-display font-bold"
              style={{ color: "#2d1810" }}
              data-testid="giftcard-success-title"
            >
              {t("gc_success_title")}
            </h1>
          </div>

          <p className="text-lg mb-8" style={{ color: "#4a3728" }}>
            {t("gc_success_subtitle")}
          </p>

          {cardCode && (
            <div
              className="rounded-xl p-8 mb-8"
              style={{ background: "#f4e7d8", border: "2px solid #d4af37" }}
            >
              <p className="text-sm mb-2" style={{ color: "#4a3728" }}>{t("gc_success_code")}</p>
              <p
                className="text-3xl font-bold font-mono tracking-wider mb-4"
                style={{ color: "#721111" }}
                data-testid="gift-card-code"
              >
                {cardCode}
              </p>

              <Button
                onClick={handleDownloadPDF}
                className="btn-maroon rounded-full px-8 py-6 font-bold transition-transform hover:scale-105"
                data-testid="download-pdf-button"
              >
                <Download className="h-5 w-5 mr-2" />
                {t("gc_success_download")}
              </Button>

              <p className="text-xs mt-4" style={{ color: "#8b4513" }}>
                {t("gc_success_pdf_hint")}
              </p>
            </div>
          )}

          <div
            className="rounded-xl p-6 mb-8 text-left"
            style={{ background: "#fdf8f0", border: "1px solid #b27f47" }}
          >
            <h3 className="font-serif-display font-semibold mb-3" style={{ color: "#2d1810" }}>
              {t("gc_success_how")}
            </h3>
            <ul className="space-y-2 text-sm" style={{ color: "#4a3728" }}>
              <li>- {t("gc_success_how_1")}</li>
              <li>- {t("gc_success_how_2")}</li>
              <li>- {t("gc_success_how_3")}</li>
              <li>- {t("gc_success_how_4")}</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/order")}
              className="rounded-full btn-maroon px-8 py-6 font-bold"
              data-testid="use-now-button"
            >
              {t("gc_success_use_now")}
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="rounded-full px-8 py-6"
              style={{ borderColor: "#b27f47", color: "#2d1810" }}
              data-testid="back-home-giftcard-button"
            >
              {t("gc_success_home")}
            </Button>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default GiftCardSuccessPage;
